/**
 * TestLinkPage - Handles /t/:code short links
 *
 * Flow:
 *  1. Not authenticated → redirect to /login?redirect=/t/:code
 *  2. Authenticated → resolve link to get assessment_id
 *  3. Check enrollment status for that quiz
 *     a. Enrolled + active plan → navigate to quiz player
 *     b. Enrolled + requires plan → show plan chooser for their batch
 *     c. Not enrolled → show BatchPlanChooser so they can pick a batch + plan
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { resolveTestLink } from '@/api/test-links';
import { getEnrollmentStatus } from '@/api/batch';
import type { BatchWithPlans } from '@/api/batch';
import { BatchPlanChooser } from '@/components/enrollment/BatchPlanChooser';
import './TestLinkPage.css';

export function TestLinkPage() {
  const { code } = useParams<{ code: string }>();
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  const [quizId, setQuizId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showChooser, setShowChooser] = useState(false);
  const [availableBatches, setAvailableBatches] = useState<BatchWithPlans[]>([]);

  useEffect(() => {
    if (isLoading || !code) return;

    if (!isAuthenticated) {
      navigate(`/login?redirect=/t/${code}`, { replace: true });
      return;
    }

    const run = async () => {
      try {
        // Step 1: Resolve the short link
        const link = await resolveTestLink(code);
        const qid = link.assessment_id;
        setQuizId(qid);

        // Step 2: Check enrollment status
        const status = await getEnrollmentStatus(qid);

        if (status.enrolled) {
          if (status.plan_status === 'active' || status.plan_status === 'no_plan_required') {
            navigate(`/student/quiz/${qid}`, { replace: true });
          } else if (status.plan_status === 'requires_plan') {
            // Enrolled in a batch but no active plan — show available plans for that batch
            setAvailableBatches(status.available_batches.length > 0
              ? status.available_batches
              : []);
            setShowChooser(true);
          } else {
            // expired / denied
            setError(
              status.plan_status === 'expired'
                ? 'Your plan for this batch has expired. Please renew or purchase a new plan.'
                : 'Access denied. Please contact your instructor.',
            );
          }
        } else {
          // Not enrolled — show the batch/plan chooser
          if (status.available_batches.length === 0) {
            setError('This assessment is not available for enrollment. Please contact your instructor.');
            return;
          }

          // Auto-enroll into default batch + free plan if one exists
          const defaultBatch = status.available_batches.find((b) => b.is_default);
          if (defaultBatch) {
            const hasFreePlan = defaultBatch.plans.some((p) => Number(p.price_amount) === 0);
            if (!defaultBatch.plans.length || hasFreePlan) {
              // Default free path — enroll silently and proceed
              const { studentEnrollInBatch } = await import('@/api/batch');
              await studentEnrollInBatch(defaultBatch.id);
              navigate(`/student/quiz/${qid}`, { replace: true });
              return;
            }
          }

          // Multiple batches or all paid — show chooser
          setAvailableBatches(status.available_batches);
          setShowChooser(true);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Invalid or expired test link.');
      }
    };

    run();
  }, [code, isAuthenticated, isLoading, navigate]);

  if (error) {
    return (
      <div className="test-link-page">
        <AlertCircle size={32} className="test-link-page__error-icon" />
        <p className="test-link-page__error">{error}</p>
      </div>
    );
  }

  if (showChooser && quizId) {
    return (
      <BatchPlanChooser
        quizId={quizId}
        availableBatches={availableBatches}
        onEnrolled={() => navigate(`/student/quiz/${quizId}`, { replace: true })}
      />
    );
  }

  return (
    <div className="test-link-page">
      <Loader2 size={32} className="test-link-page__spinner" />
      <p className="test-link-page__message">Loading assessment...</p>
    </div>
  );
}
